import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { tr } from 'i18n'
import { checkDriverAssignmentInn, registerTransportDriver } from 'service/AdminPanelService'
import { getErpErrorMessage } from 'service/ErpService'
import DocumentUpload from 'components/Erp/DocumentUpload'
import ActorPinField from 'components/Erp/ActorPinField/ActorPinField.tsx'
import s from '../Management.module.scss'
import './DriverAssignmentRegistration.scss'

const documents = [
  ['passport_front', 'Паспорт · лицевая сторона'], ['passport_back', 'Паспорт · обратная сторона'],
  ['license_front', 'Водительское удостоверение · лицевая сторона'], ['license_back', 'Водительское удостоверение · обратная сторона'],
]

export default function DriverAssignmentRegistration({ driver, busy, onRun, onClose, onExisting }: any) {
  useTranslation()
  const [form, setForm] = useState({ inn: '', first_name: '', last_name: '', middle_name: '', phone: '' })
  const [files, setFiles] = useState<Record<string, File>>({})
  const [pin, setPin] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const submitRef = useRef(false)
  const [checking, setChecking] = useState(false)
  const [check, setCheck] = useState<any>(null)
  const [error, setError] = useState('')
  const sequence = useRef(0)
  useEffect(() => () => { sequence.current++ }, [])
  const pending = busy || submitting
  const change = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    if (name === 'inn') { sequence.current++; setCheck(null); setChecking(false) }
    setError('')
    setForm(previous => ({ ...previous, [name]: name === 'inn' ? value.replace(/\D/g, '').slice(0, 14) : value }))
  }
  const checkInn = async () => {
    if (!/^[0-9]{14}$/.test(form.inn)) return null
    const ticket = ++sequence.current
    setChecking(true); setError('')
    try {
      const { data } = await checkDriverAssignmentInn(form.inn)
      if (sequence.current !== ticket) return null
      setCheck(data)
      if (data.exists) setError(tr(data.assigned ? 'Сначала освободите водителя от текущего транспорта.' : 'ИНН уже зарегистрирован. Выберите существующего водителя из списка.'))
      return data
    } catch (failure) { if (sequence.current === ticket) setError(getErpErrorMessage(failure)); return null }
    finally { if (sequence.current === ticket) setChecking(false) }
  }
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy || checking || submitRef.current || !/^[0-9]{4}$/.test(pin)) return
    if (documents.some(([field]) => !files[field])) { setError(tr('Добавьте обе стороны паспорта и водительского удостоверения.')); return }
    submitRef.current = true
    setSubmitting(true)
    try {
      const result = await checkInn()
      if (!result || result.exists) return
      const payload = new FormData()
      Object.entries(form).forEach(([key, value]) => payload.append(key, value.trim()))
      Object.entries(files).forEach(([key, file]) => payload.append(key, file))
      payload.append('expected_employee_id', String(driver.employee ?? ''))
      payload.append('actor_pin', pin)
      await onRun(() => registerTransportDriver(driver.id, payload))
    } finally {
      submitRef.current = false
      setSubmitting(false)
    }
  }
  return <form className="driver-assignment-registration" onSubmit={submit}>
    <p className={s.muted}>{tr('Данные, фотография и технический паспорт автомобиля сохраняются. Добавьте личные данные и документы нового водителя.')}</p>
    {error && <p role="alert" className={s.error}>{tr(error)}</p>}
    {check?.available_person && <button type="button" className={s.button} disabled={pending} onClick={() => onExisting(check.available_person)}>{tr('Выбрать существующего водителя')}</button>}
    <div className={s.grid}>
      <label className={s.field}>{tr('ИНН')}<input name="inn" required pattern="[0-9]{14}" inputMode="numeric" maxLength={14}
        disabled={pending} value={form.inn} onChange={change} onBlur={checkInn} /></label>
      <label className={s.field}>{tr('Фамилия')}<input name="last_name" required maxLength={255} disabled={pending} value={form.last_name} onChange={change} /></label>
      <label className={s.field}>{tr('Имя')}<input name="first_name" required maxLength={255} disabled={pending} value={form.first_name} onChange={change} /></label>
      <label className={s.field}>{tr('Отчество')}<input name="middle_name" maxLength={255} disabled={pending} value={form.middle_name} onChange={change} /></label>
      <label className={s.field}>{tr('Телефон')}<input name="phone" type="tel" required minLength={9} maxLength={30} disabled={pending} value={form.phone} onChange={change} /></label>
    </div>
    {checking && <p role="status">{tr('Проверка ИНН…')}</p>}
    <fieldset disabled={pending}><legend>{tr('Документы водителя')}</legend><div className="driver-assignment-registration__documents">
      {documents.map(([field, label]) => <DocumentUpload key={field} label={tr(label)} required value={files[field]}
        onChange={(file: File) => setFiles(previous => ({ ...previous, [field]: file }))} />)}
    </div></fieldset>
    <ActorPinField value={pin} onChange={setPin} disabled={pending} />
    <div className={s.footer}><button type="button" className={s.button} disabled={pending || checking} onClick={onClose}>{tr('Отмена')}</button>
      <button className={s.primary} disabled={pending || checking || Boolean(check?.exists) || !/^[0-9]{4}$/.test(pin)}>{tr('Зарегистрировать и назначить')}</button></div>
  </form>
}
